import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clearOffline,
  loadOffline,
  saveOffline,
} from '@/hooks/useOfflineStorage';

const DEFAULT_AUTOSAVE_DELAY_MS = 60_000;
const DEFAULT_LOCAL_BACKUP_DELAY_MS = 2_000;

export interface UseAutosaveOptions<T> {
  /** Stable offline storage key for localStorage backup + recovery. */
  offlineKey: string;
  /** Whether the browser is currently online. Drives offline-save path + reconnect flush. */
  isOnline: boolean;
  /** Persist `value` to the server. Called by manual save, autosave, and offline-reconnect flush. */
  saveFn: (value: T) => Promise<void>;
  /** Autosave debounce (ms). Defaults to 60s — matches the breakdown UX. */
  autosaveDelayMs?: number;
  /** Local backup debounce (ms). Defaults to 2s. */
  localBackupDelayMs?: number;
  /**
   * Label used for the downloaded JSON backup file + `kind` field in the
   * backup payload. Appears in the filename: `{backupKind}-backup-{id}-{ts}.json`.
   */
  backupKind?: string;
}

export interface UseAutosaveReturn<T> {
  /** Current pending (dirty) value, or null if clean. */
  pending: T | null;
  /** Mark a new value as pending. Triggers autosave + local backup timers. */
  setPending: (next: T) => void;
  /** Clear pending state without saving. Also clears the localStorage backup. */
  discardPending: () => void;
  isDirty: boolean;
  isSaving: boolean;
  hasOfflineEdits: boolean;
  saveError: string | null;
  /** Manual save — safe to call redundantly; returns the in-flight promise if one exists. */
  save: () => Promise<void>;
  /** Await any in-flight save; if dirty, save now. Rejects on a newly-raised save error. */
  flushPendingSave: () => Promise<void>;
  /** Download current pending state (or `[]` for carpet-shaped backups) as a JSON file. */
  downloadBackup: (id: string, payloadBuilder?: (value: T | null) => unknown) => void;
  /** Offline data from a prior session, awaiting recovery. */
  pendingRecovery: T | null;
  acceptRecovery: () => void;
  discardRecovery: () => void;
}

/**
 * Value-agnostic autosave mechanics. Owns:
 *   - 60s autosave debounce + 2s localStorage backup debounce
 *   - In-flight promise guard (manual/auto saves dedupe)
 *   - `saveError` surface + fallback write to localStorage on error
 *   - Offline write path (`navigator.onLine === false`) + on-reconnect flush
 *   - On-mount recovery detection (`pendingRecovery`)
 *
 * Consumers provide the `saveFn` — what "persisting to the server" means for
 * their data. Everything else is handled identically for every caller.
 */
export function useAutosave<T>(options: UseAutosaveOptions<T>): UseAutosaveReturn<T> {
  const {
    offlineKey,
    isOnline,
    saveFn,
    autosaveDelayMs = DEFAULT_AUTOSAVE_DELAY_MS,
    localBackupDelayMs = DEFAULT_LOCAL_BACKUP_DELAY_MS,
    backupKind = 'autosave',
  } = options;

  const [pending, setPendingState] = useState<T | null>(null);
  const [hasOfflineEdits, setHasOfflineEdits] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingRecovery, setPendingRecovery] = useState<T | null>(null);

  // Refs mirror the latest render's values so callbacks can read fresh state
  // without re-creating identity every render.
  const pendingRef = useRef<T | null>(null);
  pendingRef.current = pending;
  const isDirtyRef = useRef(false);
  isDirtyRef.current = pending !== null;
  const saveErrorRef = useRef<string | null>(null);
  const recoveryCheckedRef = useRef(false);
  // Consumers build `saveFn` from a react-query mutation, so it is a new
  // function every render. Read it through a ref: the returned callbacks stay
  // identity-stable, so a flush-on-unmount effect keyed on `flushPendingSave`
  // runs on unmount only — not on every render, which would save per keystroke.
  const saveFnRef = useRef(saveFn);
  saveFnRef.current = saveFn;

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const localBackupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveInFlightPromiseRef = useRef<Promise<void> | null>(null);

  const clearAutosaveTimer = useCallback(() => {
    if (autosaveTimerRef.current !== null) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }, []);

  const persistToLocalStorage = useCallback(() => {
    if (localBackupTimerRef.current !== null) {
      clearTimeout(localBackupTimerRef.current);
    }
    localBackupTimerRef.current = setTimeout(() => {
      const current = pendingRef.current;
      if (current !== null) {
        saveOffline(offlineKey, current);
      }
    }, localBackupDelayMs);
  }, [offlineKey, localBackupDelayMs]);

  const performSave = useCallback(async () => {
    const target = pendingRef.current;
    if (target === null) return;

    if (!navigator.onLine) {
      saveOffline(offlineKey, target);
      setHasOfflineEdits(true);
      setPendingState(null);
      pendingRef.current = null;
      setSaveError(null);
      saveErrorRef.current = null;
      return;
    }

    setIsSaving(true);
    try {
      await saveFnRef.current(target);
      setPendingState(null);
      pendingRef.current = null;
      setHasOfflineEdits(false);
      clearOffline(offlineKey);
      setSaveError(null);
      saveErrorRef.current = null;
      if (localBackupTimerRef.current !== null) {
        clearTimeout(localBackupTimerRef.current);
        localBackupTimerRef.current = null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setSaveError(message);
      saveErrorRef.current = message;
      // Persist to localStorage as a safety net so the user doesn't lose edits
      saveOffline(offlineKey, target);
    } finally {
      setIsSaving(false);
    }
  }, [offlineKey]);

  // Single entry point for all save triggers — wraps `performSave` with
  // in-flight promise tracking so redundant calls (manual + autosave firing
  // on the same tick, for example) dedupe onto one promise.
  const runSave = useCallback((): Promise<void> => {
    if (saveInFlightPromiseRef.current) return saveInFlightPromiseRef.current;
    clearAutosaveTimer();
    const promise = performSave().finally(() => {
      saveInFlightPromiseRef.current = null;
    });
    saveInFlightPromiseRef.current = promise;
    return promise;
  }, [clearAutosaveTimer, performSave]);

  const scheduleAutosave = useCallback(() => {
    clearAutosaveTimer();
    autosaveTimerRef.current = setTimeout(() => {
      if (!navigator.onLine) return;
      if (saveInFlightPromiseRef.current) {
        scheduleAutosave();
        return;
      }
      if (isDirtyRef.current) {
        void runSave();
      }
    }, autosaveDelayMs);
  }, [autosaveDelayMs, clearAutosaveTimer, runSave]);

  const flushPendingSave = useCallback(async (): Promise<void> => {
    const priorError = saveErrorRef.current;
    if (saveInFlightPromiseRef.current) {
      await saveInFlightPromiseRef.current;
    } else if (isDirtyRef.current) {
      await runSave();
    }
    if (saveErrorRef.current && saveErrorRef.current !== priorError) {
      throw new Error(`Save failed: ${saveErrorRef.current}`);
    }
  }, [runSave]);

  const setPending = useCallback(
    (next: T) => {
      setPendingState(next);
      pendingRef.current = next;
      scheduleAutosave();
      persistToLocalStorage();
    },
    [scheduleAutosave, persistToLocalStorage],
  );

  const discardPending = useCallback(() => {
    setPendingState(null);
    pendingRef.current = null;
    clearAutosaveTimer();
    clearOffline(offlineKey);
    setHasOfflineEdits(false);
  }, [clearAutosaveTimer, offlineKey]);

  const downloadBackup = useCallback(
    (id: string, payloadBuilder?: (value: T | null) => unknown) => {
      const payload = payloadBuilder
        ? payloadBuilder(pendingRef.current)
        : {
            version: 1,
            kind: backupKind,
            exportedAt: new Date().toISOString(),
            id,
            value: pendingRef.current,
          };
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${backupKind}-backup-${id}-${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, '-')}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [backupKind],
  );

  // On mount, surface any offline data from a previous session.
  useEffect(() => {
    if (recoveryCheckedRef.current) return;
    recoveryCheckedRef.current = true;
    const offlineData = loadOffline<T>(offlineKey);
    if (offlineData !== null && offlineData !== undefined) {
      setPendingRecovery(offlineData);
    }
  }, [offlineKey]);

  // When coming back online with pending offline edits saved during an
  // offline session, flush them to the server.
  useEffect(() => {
    if (!isOnline || !hasOfflineEdits) return;
    const offlineData = loadOffline<T>(offlineKey);
    if (offlineData !== null && offlineData !== undefined) {
      pendingRef.current = offlineData;
      setPendingState(offlineData);
      void runSave();
    } else {
      setHasOfflineEdits(false);
    }
  }, [isOnline, hasOfflineEdits, offlineKey, runSave]);

  const acceptRecovery = useCallback(() => {
    const recovered = pendingRecovery;
    if (recovered === null || recovered === undefined) return;
    pendingRef.current = recovered;
    setPendingState(recovered);
    setPendingRecovery(null);
    if (navigator.onLine) {
      void runSave();
    } else {
      setHasOfflineEdits(true);
    }
  }, [pendingRecovery, runSave]);

  const discardRecovery = useCallback(() => {
    clearOffline(offlineKey);
    setPendingRecovery(null);
    setHasOfflineEdits(false);
  }, [offlineKey]);

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current !== null) clearTimeout(autosaveTimerRef.current);
      if (localBackupTimerRef.current !== null) clearTimeout(localBackupTimerRef.current);
    };
  }, []);

  useEffect(() => {
    saveErrorRef.current = saveError;
  }, [saveError]);

  return {
    pending,
    setPending,
    discardPending,
    isDirty: pending !== null,
    isSaving,
    hasOfflineEdits,
    saveError,
    save: runSave,
    flushPendingSave,
    downloadBackup,
    pendingRecovery,
    acceptRecovery,
    discardRecovery,
  };
}
